/**
 * Tagada Rx — CRM-side API client.
 *
 * Thin wrapper around the `trpc.rx.*` router. All shapes are inferred
 * from `AppRouter` via `@tagadapay/trpc-types`, the same way every
 * other module in `apps/crm-app/src/lib/api/` does it. When the
 * backend router changes, the types in pages update automatically
 * once `pnpm --filter @tagadapay/trpc-types build` runs (this is
 * already part of `apps/crm-app`'s `build` script).
 */

import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@tagadapay/trpc-types';

import { trpc } from './trpc';

type RxOutputs = inferRouterOutputs<AppRouter>['rx'];
type RxInputs = inferRouterInputs<AppRouter>['rx'];

// ---------------------------------------------------------------------------
// Inferred surface types — re-exported for pages/components that need
// to type local state (filters, dropdown options, …) against the
// backend contract.
// ---------------------------------------------------------------------------

export type RxIntegrationStatus = RxOutputs['integrationStatus'];
export type RxBrandConfig = RxOutputs['brandConfig'];
export type RxClinicalNetwork = RxOutputs['listNetworks'][number];
export type RxAffiliateTerms = RxOutputs['listAffiliateTerms'][number];
export type RxAffiliateBalance = RxOutputs['affiliateBalance'];
export type RxAffiliateLedgerEntry = RxAffiliateBalance['entries'][number];
export type RxProductOffering = RxOutputs['listProductOfferings'][number];
export type RxStoreTreatment = RxOutputs['listStoreTreatments']['treatments'][number];

export type RxQuestionnaireSummary = RxOutputs['listQuestionnaires']['questionnaires'][number];
export type RxClinicalQuestion = RxOutputs['questionnaireQuestions']['questions'][number];

export type RxCasesPage = RxOutputs['listCases'];
export type RxCase = RxCasesPage['rows'][number];
export type RxCaseStatus = RxCase['status'];

export type RxCaseDetail = RxOutputs['getCase'];
export type RxCaseEvent = RxCaseDetail['events'][number];
export type RxCaseEconomics = RxCaseDetail['economics'];

export type RxStoreDashboard = RxOutputs['dashboard'];
export type RxStoreFeeSchedule = RxOutputs['feeSchedule'];

export type RxListCasesInput = RxInputs['listCases'];
export type RxActivationInput = RxInputs['requestActivation'];
export type RxConfirmSetupFeeInput = RxInputs['confirmSetupFeePaid'];
export type RxConfigureBrandClinicalInput = RxInputs['configureBrandClinical'];
export type RxSetAffiliateTermsInput = RxInputs['setAffiliateTerms'];
export type RxSetProductOfferingInput = RxInputs['setProductOffering'];
export type RxStorefrontProfile = RxOutputs['getStorefront'];
export type RxPayoutProfile = RxOutputs['payoutProfile'];
export type RxActivationStatus = RxOutputs['activationStatus'];
export type RxStatementDescriptor = RxOutputs['statementDescriptor'];
export type RxPayoutKycStatus = RxPayoutProfile['profile']['kycStatus'];
export type RxSavePayoutBankAccountInput = RxInputs['savePayoutBankAccount'];
export type RxPayoutMethod = RxSavePayoutBankAccountInput['method'];

export type RxOperatingMode = NonNullable<RxActivationInput['operatingMode']>;
export type RxAffiliateTermsBasis = RxAffiliateTerms['basis'];

// ---------------------------------------------------------------------------
// Integration lifecycle
// ---------------------------------------------------------------------------

export const useRxIntegrationStatus = (storeId: string, opts?: { enabled?: boolean }) =>
  trpc.rx.integrationStatus.useQuery(
    { storeId },
    { enabled: (opts?.enabled ?? true) && !!storeId, staleTime: 15_000 },
  );

export const useRxRequestActivation = () => trpc.rx.requestActivation.useMutation();

export const useRxConfirmSetupFeePaid = () => trpc.rx.confirmSetupFeePaid.useMutation();

// ---------------------------------------------------------------------------
// Brand clinical config
// ---------------------------------------------------------------------------

export const useRxBrandConfig = (storeId: string) =>
  trpc.rx.brandConfig.useQuery({ storeId }, { enabled: !!storeId, staleTime: 30_000 });

export const useRxConfigureBrandClinical = () => trpc.rx.configureBrandClinical.useMutation();

export const useRxSetAllowedStates = () => trpc.rx.setAllowedStates.useMutation();

// ---------------------------------------------------------------------------
// Clinical networks catalog
// ---------------------------------------------------------------------------

export const useRxNetworks = () => trpc.rx.listNetworks.useQuery(undefined, { staleTime: 60_000 });

// ---------------------------------------------------------------------------
// Affiliate terms
// ---------------------------------------------------------------------------

export const useRxAffiliateTerms = (storeId: string) =>
  trpc.rx.listAffiliateTerms.useQuery({ storeId }, { enabled: !!storeId, staleTime: 30_000 });

export const useRxSetAffiliateTerms = () => trpc.rx.setAffiliateTerms.useMutation();

/** Owed-money tracking — ledger balance + entry trail for the store. */
export const useRxAffiliateBalance = (storeId: string) =>
  trpc.rx.affiliateBalance.useQuery({ storeId }, { enabled: !!storeId, staleTime: 30_000 });

/** Staff: approve / mark paid / cancel a brand payout. */
export const useRxPayoutAction = () => trpc.rx.payoutAction.useMutation();

/** Brand statement: sales, deductions with their source, payouts, owed now. */
export const useRxStatement = (storeId: string, range?: { from?: string; to?: string }) =>
  trpc.rx.statement.useQuery({ storeId, ...(range ?? {}) }, { enabled: !!storeId, staleTime: 30_000 });

// ---------------------------------------------------------------------------
// Payout profile (legal-rep KYC + bank account)
// ---------------------------------------------------------------------------

/**
 * Both payout gates for the signed-in account. Polls while the SumSub
 * review is running so the step advances on its own once the webhook lands.
 */
export const useRxPayoutProfile = (opts?: { poll?: boolean }) =>
  trpc.rx.payoutProfile.useQuery(undefined, {
    staleTime: 10_000,
    refetchInterval: opts?.poll ? 15_000 : false,
  });

export const useRxStartPayoutKyc = () => trpc.rx.startPayoutKyc.useMutation();

export const useRxRefreshPayoutKyc = () => trpc.rx.refreshPayoutKyc.useMutation();

export const useRxSavePayoutBankAccount = () => trpc.rx.savePayoutBankAccount.useMutation();

export const useRxBypassPayoutKyc = () => trpc.rx.bypassPayoutKyc.useMutation();

// ---------------------------------------------------------------------------
// Product offerings
// ---------------------------------------------------------------------------

export const useRxProductOfferings = () =>
  trpc.rx.listProductOfferings.useQuery(undefined, { staleTime: 30_000 });

export const useRxSetProductOffering = () => trpc.rx.setProductOffering.useMutation();

export const useRxStoreTreatments = (storeId: string) =>
  trpc.rx.listStoreTreatments.useQuery({ storeId }, { enabled: !!storeId, staleTime: 15_000 });

export const useRxSetTreatmentPrice = () => trpc.rx.setTreatmentPrice.useMutation();

export const useRxSetTreatmentActive = () => trpc.rx.setTreatmentActive.useMutation();

/** Tagada staff: approve or reject a brand's add-on request (memberships…). */
export const useRxReviewAddon = () => trpc.rx.reviewAddon.useMutation();

export type RxWeightLossProgram = RxOutputs['getWeightLossProgram'];

export const useRxWeightLossProgram = (storeId: string, opts?: { enabled?: boolean }) =>
  trpc.rx.getWeightLossProgram.useQuery(
    { storeId },
    { enabled: !!storeId && (opts?.enabled ?? true), staleTime: 15_000 },
  );

export const useRxSetWeightLossProgram = () => trpc.rx.setWeightLossProgram.useMutation();

// ---------------------------------------------------------------------------
// Payments setup — activation status + statement descriptor
// ---------------------------------------------------------------------------

/**
 * Steps, missing items and "Rx payments: setting up / ready". Polls while
 * payments are still being set up so the chip flips on its own.
 */
export const useRxActivationStatus = (storeId: string, opts?: { enabled?: boolean }) =>
  trpc.rx.activationStatus.useQuery(
    { storeId },
    {
      enabled: (opts?.enabled ?? true) && !!storeId,
      staleTime: 15_000,
      refetchInterval: (query) => (query.state.data?.payments === 'setting_up' ? 60_000 : false),
    },
  );

export const useRxStatementDescriptor = (storeId: string, opts?: { enabled?: boolean }) =>
  trpc.rx.statementDescriptor.useQuery(
    { storeId },
    { enabled: (opts?.enabled ?? true) && !!storeId, staleTime: 15_000, retry: false },
  );

export const useRxSetStatementDescriptor = () => trpc.rx.setStatementDescriptor.useMutation();

export const useRxReviewStatementDescriptor = () => trpc.rx.reviewStatementDescriptor.useMutation();

export const useRxStorefrontTemplates = () =>
  trpc.rx.storefrontTemplates.useQuery(undefined, { staleTime: 300_000 });

/** Plugins the account may publish as a custom storefront (own or public, Rx-capable). */
export const useRxStorefrontPlugins = (enabled = true) =>
  trpc.rx.storefrontPlugins.useQuery(undefined, { enabled, staleTime: 30_000 });

export const useRxStorefront = (storeId: string) =>
  trpc.rx.getStorefront.useQuery({ storeId }, { enabled: !!storeId, staleTime: 15_000 });

export const useRxSaveStorefront = () => trpc.rx.saveStorefront.useMutation();

export const useRxPublishStorefront = () => trpc.rx.publishStorefront.useMutation();

export const useRxSupportMailbox = (storeId: string) =>
  trpc.rxSupport.storeMailbox.useQuery({ storeId }, { enabled: !!storeId, staleTime: 15_000 });

export const useRxRequestCustomSupportDomain = () => trpc.rxSupport.requestCustomDomain.useMutation();

export const useRxCheckSupportMailbox = () => trpc.rxSupport.checkMailbox.useMutation();

export const useRxRemoveCustomSupportDomain = () => trpc.rxSupport.removeCustomDomain.useMutation();

export const useRxPrecheckCustomSupportDomain = (domain: string, opts?: { enabled?: boolean }) =>
  trpc.rxSupport.precheckCustomDomain.useQuery(
    { domain },
    { enabled: (opts?.enabled ?? true) && domain.trim().length > 3, staleTime: 10_000 },
  );

// ---------------------------------------------------------------------------
// Clinical questionnaires (network-authored intake forms)
// ---------------------------------------------------------------------------

/** Catalog of questionnaires on the store's clinical network (e.g. MDI). */
export const useRxQuestionnaires = (storeId: string, opts?: { enabled?: boolean }) =>
  trpc.rx.listQuestionnaires.useQuery(
    { storeId },
    { enabled: (opts?.enabled ?? true) && !!storeId, staleTime: 60_000, retry: 1 },
  );

/** Questions of one network questionnaire (read-only preview). */
export const useRxQuestionnaireQuestions = (
  storeId: string,
  externalQuestionnaireId: string | null | undefined,
) =>
  trpc.rx.questionnaireQuestions.useQuery(
    { storeId, externalQuestionnaireId: externalQuestionnaireId ?? '' },
    { enabled: !!storeId && !!externalQuestionnaireId, staleTime: 60_000, retry: 1 },
  );

// ---------------------------------------------------------------------------
// Economics — dashboard + cost transparency
// ---------------------------------------------------------------------------

/** Store dashboard aggregates (pipeline, KPIs, revenue series, costs). */
export const useRxDashboard = (storeId: string, days: number, opts?: { enabled?: boolean }) =>
  trpc.rx.dashboard.useQuery(
    { storeId, days },
    { enabled: (opts?.enabled ?? true) && !!storeId, staleTime: 30_000 },
  );

/** "What you pay": network encounter fees + per-medication pharmacy costs. */
export const useRxFeeSchedule = (storeId: string, opts?: { enabled?: boolean }) =>
  trpc.rx.feeSchedule.useQuery(
    { storeId },
    { enabled: (opts?.enabled ?? true) && !!storeId, staleTime: 60_000 },
  );

export const useRxSetNetworkFeeSchedule = () => trpc.rx.setNetworkFeeSchedule.useMutation();

export const useRxSetOfferingCost = () => trpc.rx.setOfferingCost.useMutation();

// ---------------------------------------------------------------------------
// Cases
// ---------------------------------------------------------------------------

export const useRxCases = (params: RxListCasesInput) =>
  trpc.rx.listCases.useQuery(params, { staleTime: 15_000 });

export const useRxCase = (caseId: string | undefined) =>
  trpc.rx.getCase.useQuery({ caseId: caseId ?? '' }, { enabled: !!caseId, staleTime: 5_000 });

// ---------------------------------------------------------------------------
// Display helpers (presentation only, no type coupling)
// ---------------------------------------------------------------------------

export const formatCents = (minor: number, currency: string): string => {
  const value = (minor ?? 0) / 100;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
};

export const caseStatusBadgeColor = (status: RxCaseStatus): string => {
  switch (status) {
    case 'requested':
    case 'submitted':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'in_review':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'approved':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'shipped':
      return 'bg-purple-50 text-purple-700 border-purple-200';
    case 'delivered':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'declined':
    case 'error':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'cancelled':
      return 'bg-gray-50 text-gray-600 border-gray-200';
    default:
      return 'bg-gray-50 text-gray-600 border-gray-200';
  }
};
